<template>
  <div class="view" v-if="canEdit || canDelete" v-cloak>
    <GridPanelHeader
      :title="$t('works.cultures.name')"
      :grid-ref="$refs.culturesGrid"
      :show-add="canEdit"
      :show-filter="false"
      :show-refresh="true"
      @add="onAddCulture"
    />
    <div class="unassigned-alert alert alert-warning mt-2" v-if="apiConfig && unassignedRows.length > 0">
      <img src="~@/assets/images/desktop/icons/controls/alert.svg" class="icon-svg text-dark pr-2" />
      <span
        @click="syncCultures"
        v-html="$t('works.cultures.you_have_unassigned_cultures_link', { count: unassignedRows.length })"
      />
      <div class="load text-center" v-if="syncLoading">
        <loader :loading="syncLoading" size="sm" />
      </div>
    </div>
    <DxDataGrid
      ref="culturesGrid"
      :show-column-lines="true"
      :show-row-lines="false"
      :column-auto-width="true"
      :allow-column-reordering="true"
      :allow-column-resizing="true"
      :row-alternation-enabled="true"
      :show-borders="true"
      :data-source="dataSource"
      :remote-operations="gridRemoteOperations"
      :element-attr="gridAttributes"
      :row-height="150"
    >
      <DxColumn dataField="name" :caption="$t('common.culture')" :width="'20%'" :filter-operations="['contains']">
        <DxRequiredRule :message="$t('validation.name_is_required')" />
      </DxColumn>

      <DxColumn dataField="remote_id" :caption="$t('remote_id')" :width="'15%'" :filter-operations="['contains']" />

      <DxColumn
        data-field="fito_culture_uuid"
        :caption="$t('works.cultures.fito_culture')"
        :width="240"
        :calculate-display-value="getFitoCultureDisplayValue"
        edit-cell-template="fitoCultureEditTemplate"
      >
        <DxLookup :data-source="fitoCultures" value-expr="uuid" display-expr="name" />
      </DxColumn>
      <template #fitoCultureEditTemplate="{ data }">
        <FitoCultureSelector
          :value="data.value || null"
          :cultures="fitoCultures"
          :disabled="loadingFitoCultures"
          @input="(value) => data.setValue(value)"
        />
      </template>

      <DxColumn
        dataField="color"
        :caption="$t('common.color')"
        :width="'15%'"
        cell-template="colorCellTemplate"
        edit-cell-template="colorEditTemplate"
        :filter-operations="['contains']"
      >
      </DxColumn>

      <template #colorCellTemplate="{ data }">
        <div class="d-flex align-items-center">
          <span
            :style="{
              backgroundColor: data.value ?? 'transparent',
              width: '17px',
              height: '17px',
              display: 'inline-block',
              borderRadius: '50%',
              border: '1px solid gray',
              marginRight: '8px',
            }"
          ></span>
          <span>{{ data.value ?? "" }}</span>
        </div>
      </template>

      <template #colorEditTemplate="{ data }">
        <ColorModalPicker ref="colorPicker" v-model="data.value" @confirmColorChange="(val) => handleColorChange(val, data)" />
      </template>

      <DxColumn
        dataField="updated_at"
        :caption="$t('common.updated_at')"
        dataType="date"
        :format="getDxGridDefaultDateFormat"
        :width="'20%'"
        :formItem="{
          visible: false,
        }"
        :filter-operations="['=']"
      />

      <DxColumn
        :width="60"
        alignment="center"
        :fixed="true"
        fixed-position="right"
        :allow-sorting="false"
        :allow-filtering="false"
        :show-in-column-chooser="false"
        :formItem="{
          visible: false,
        }"
        cell-template="dynamicMenuCell"
      />
      <template #dynamicMenuCell="{ data }">
        <DxActionMenu
          :grid-ref="$refs.culturesGrid"
          row-key="uuid"
          :row="data.data"
          :can-edit="canEdit"
          :can-delete="canDelete"
          :allow-deleting="allowDeleting"
        />
      </template>

      <DxMasterDetail :enabled="true" template="cultureDetailTemplate" />
      <template #cultureDetailTemplate="{ data: culture }">
        <culture-details-template :data="culture"></culture-details-template>
      </template>

      <DxFilterRow :visible="true" />
      <DxScrolling mode="infinite" />
      <DxColumnChooser :enabled="false" mode="select" />
      <DxEditing :allow-adding="false" :allow-updating="false" :allow-deleting="false" :confirm-delete="true" mode="form" :use-icons="true">
        <DxTexts
          :confirm-delete-message="
            $t('common.confirm_delete', {
              title: $t('works.cultures.name'),
            })
          "
          :confirm-delete-title="$t('common.confirm_delete_title')"
          :deleteRow="$t('common.delete')"
        />
      </DxEditing>
    </DxDataGrid>
  </div>
  <div v-else>
    <forbidden></forbidden>
  </div>
</template>

<script>
import {
  DxDataGrid,
  DxColumn,
  DxEditing,
  DxFilterRow,
  DxScrolling,
  DxMasterDetail,
  DxRequiredRule,
  DxTexts,
  DxColumnChooser,
  DxLookup,
} from "devextreme-vue/data-grid";
import GridPanelHeader from "@/components/DxGrid/GridPanelHeader.vue";
import { mapActions, mapGetters } from "vuex";
import Forbidden from "@/pages/errors/Forbidden.vue";
import CustomStore from "devextreme/data/custom_store";
import DataSource from "devextreme/data/data_source";
import CultureDetailsTemplate from "@/pages/works/cultures/partials/CultureDetailsTemplate.vue";
import { canDelete, canEdit } from "@/helpers/Permissions";
import ColorModalPicker from "@/components/Common/ColorModalPicker.vue";
import DxActionMenu from "@/components/DxGrid/DxActionMenu.vue";
import { exportDataGrid } from "devextreme/excel_exporter";
import { Workbook } from "exceljs";
import { saveAs } from "file-saver-es";
import { format } from "date-fns";
import FitoCultureSelector from "./partials/FitoCultureSelector.vue";
import notify from "devextreme/ui/notify";
import { types } from "@/helpers/ImportTypes";
import { sourceTypes } from "@/helpers/ImportSourceTypes";
import Loader from "@/components/Loader.vue";
import { handleError } from "@/helpers/generalActions";

export default {
  name: "Cultures",
  components: {
    GridPanelHeader,
    CultureDetailsTemplate,
    Forbidden,
    DxDataGrid,
    DxColumn,
    DxEditing,
    DxFilterRow,
    DxScrolling,
    DxMasterDetail,
    DxRequiredRule,
    ColorModalPicker,
    DxTexts,
    DxColumnChooser,
    DxActionMenu,
    DxLookup,
    FitoCultureSelector,
    Loader,
  },
  data() {
    return {
      gridRemoteOperations: {
        filtering: false,
        sorting: false,
      },
      cultureTabSelectedIndex: 0,
      fitoCultures: [],
      loadingFitoCultures: false,
      syncLoading: false,
      lastLoadedItems: [],
    };
  },
  computed: {
    ...mapGetters({
      getDxGridDefaultDateFormat: "settings/getDxGridDefaultDateFormat",
      importConfigurations: "importConfig/importConfigurations",
    }),
    gridAttributes() {
      return {
        class: "smagria-grid cultures-list-grid" + (this.apiConfig && this.unassignedRows.length > 0 ? " with-alert" : ""),
      };
    },
    dataSource() {
      return new DataSource({
        store: new CustomStore({
          key: "uuid",
          load: this.load,
          insert: this.create,
          update: this.update,
          remove: this.remove,
        }),
      });
    },
    canEdit() {
      return canEdit(this);
    },
    canDelete() {
      return canDelete(this);
    },
    apiConfig() {
      return this.importConfigurations?.find(
        (config) =>
          config.import_type === types.SGPS_DASC_IMPORT.id &&
          config.is_active === 1 &&
          config.source_type === sourceTypes.API_IMPORT.id &&
          config.source_config_uuid,
      );
    },
    unassignedRows() {
      return Array.isArray(this.lastLoadedItems) ? this.lastLoadedItems.filter((item) => !item.remote_id) : [];
    },
  },
  methods: {
    ...mapActions({
      get: "works-cultures/getCultures",
      create: "works-cultures/createCulture",
      change: "works-cultures/updateCulture",
      delete: "works-cultures/deleteCulture",
      getFitoCultures: "works-cultures/getFitoCultures",
      syncTracktorApiCrops: "importConfig/syncTracktorApiCrops",
      getImportConfigurations: "importConfig/getImportConfigurations",
    }),
    async loadFitoCultures() {
      this.loadingFitoCultures = true;
      try {
        const cultures = await this.getFitoCultures({
          limit: 2500,
        });
        if (!Array.isArray(cultures)) {
          throw new TypeError("Invalid fito culture catalogue response");
        }
        this.fitoCultures = cultures;
      } catch (error) {
        notify(this.$t("error_alert_text"), "error", 5000);
      } finally {
        this.loadingFitoCultures = false;
      }
    },
    getFitoCultureDisplayValue(row) {
      if (!row.fito_culture_uuid) {
        return "";
      }
      return this.fitoCultures.find((culture) => culture.uuid === row.fito_culture_uuid)?.name || row.fito_culture_name || "";
    },
    async load(data) {
      let dataSource = {
        data: [],
        totalCount: 0,
      };

      await this.get({
        filter: data.filter,
        sort: data.sort,
        limit: 9999,
        enablePagination: 0,
      }).then((resp) => {
        dataSource.data = resp.data;
        this.lastLoadedItems = resp.data;
        dataSource.totalCount = 1;
      });

      return dataSource;
    },
    async update(key, obj) {
      const updatable = Object.assign(
        {},
        this.dataSource.items().find((item) => item.uuid === key),
        obj,
      );
      await this.change(updatable);
    },
    async remove(uuid) {
      if (this.dataSource.items().find((item) => item.uuid === uuid)?.is_deletable === 0) return;

      await this.delete({
        uuid: uuid,
      });
    },
    allowDeleting(e) {
      const rowData = e && e.row ? e.row.data : e;
      return this.canDelete && rowData?.is_deletable === 1;
    },
    handleColorChange(newColor, data) {
      data.setValue(newColor);
    },
    onExporting(e) {
      e.fileName = "Cultures_" + format(new Date(), "yyyy_MM_dd_hh_mm_ss");
      e.cancel = true;

      if (e.format === "xlsx") {
        const workbook = new Workbook();
        const worksheet = workbook.addWorksheet("Cultures");

        exportDataGrid({
          component: e.component,
          worksheet,
          autoFilterEnabled: true,
        }).then(() => {
          workbook.xlsx.writeBuffer().then((buffer) => {
            saveAs(new Blob([buffer], { type: "application/octet-stream" }), e.fileName + ".xlsx");
          });
        });
      }
    },
    onAddCulture() {
      this.$refs.culturesGrid.instance.addRow();
    },
    syncCultures(event) {
      if (!event.target.classList.contains("clickable") || this.syncLoading) {
        return;
      }

      this.syncLoading = true;
      this.syncTracktorApiCrops({ import_configuration_uuid: this.apiConfig.uuid })
        .then(() => this.$refs.culturesGrid?.instance?.refresh())
        .catch((error) => handleError(this, error))
        .finally(() => {
          this.syncLoading = false;
        });
    },
  },
  mounted() {
    this.loadFitoCultures();
    this.getImportConfigurations({
      import_type: types.SGPS_DASC_IMPORT.id,
      source_type: sourceTypes.API_IMPORT.id,
    });
  },
};
</script>
<style>
.cultures-list-grid.with-alert {
  height: calc(95vh - 210px);
}

body.modal-open {
  overflow: hidden !important;
}
</style>
